import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
// @flow

/*
 * Licensed Materials - Property of IBM
 * 5724-Q36
 * (c) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import { Draggable16 } from '@carbon/icons-react';
import { useDrag, useDrop } from 'react-dnd';
import cx from 'classnames';
import { pkg } from '../../../settings';
var useEffect = React.useEffect,
    useRef = React.useRef,
    useState = React.useState;
var blockClass = "".concat(pkg.prefix, "--datagrid");
var DRAG_TYPE = "".concat(blockClass, "__shared-ui-draggable-element");

var DraggableElement = function DraggableElement(_ref) {
  var _cx;

  var id = _ref.id,
      index = _ref.index,
      listData = _ref.listData,
      children = _ref.children,
      type = _ref.type,
      disabled = _ref.disabled,
      ariaLabel = _ref.ariaLabel,
      onGrab = _ref.onGrab,
      onArrowKeyDown = _ref.onArrowKeyDown,
      isFocused = _ref.isFocused,
      moveElement = _ref.moveElement,
      selected = _ref.selected,
      _ref$positionLabel = _ref.positionLabel,
      positionLabel = _ref$positionLabel === void 0 ? 'Current position {index} of {total}' : _ref$positionLabel,
      _ref$grabbedLabel = _ref.grabbedLabel,
      grabbedLabel = _ref$grabbedLabel === void 0 ? '{itemName} grabbed.' : _ref$grabbedLabel,
      _ref$droppedLabel = _ref.droppedLabel,
      droppedLabel = _ref$droppedLabel === void 0 ? '{itemName} dropped.' : _ref$droppedLabel;
  var ref = useRef();

  var _useDrop = useDrop({
    accept: DRAG_TYPE + type,
    collect: function collect(monitor) {
      return {
        isOver: !!monitor.isOver()
      };
    },
    drop: function drop(item) {
      moveElement(item.index, index);
    },
    canDrop: function canDrop() {
      return !disabled;
    },
    hover: function hover(item) {
      var dragIndex = item.index;
      var hoverIndex = index; // Don't replace items with themselves

      if (dragIndex === hoverIndex || disabled) {
        return;
      }

      moveElement(dragIndex, hoverIndex); // Time to actually perform the action
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign

      item.index = hoverIndex;
    }
  }),
      _useDrop2 = _slicedToArray(_useDrop, 2),
      isOver = _useDrop2[0].isOver,
      drop = _useDrop2[1];

  var _useDrag = useDrag({
    type: DRAG_TYPE + type,
    item: {
      id: id,
      index: index
    },
    canDrag: function canDrag() {
      return !disabled;
    },
    collect: function collect(monitor) {
      return {
        isDragging: monitor.isDragging()
      };
    }
  }),
      _useDrag2 = _slicedToArray(_useDrag, 3),
      isDragging = _useDrag2[0].isDragging,
      drag = _useDrag2[1],
      preview = _useDrag2[2];

  useEffect(function () {
    if (isFocused && ref && ref.current) {
      ref.current.focus();
    }
  }, [isFocused]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isGrabbed = _useState2[0],
      setIsGrabbed = _useState2[1];

  var _useState3 = useState(isFocused),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocusedOnItem = _useState4[0],
      setIsFocusedOnItem = _useState4[1];

  drop(ref);
  var content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cx({
      disabled: disabled
    }, "".concat(blockClass, "__draggable-handleStyle"))
  }, /*#__PURE__*/React.createElement(Draggable16, null)), children);
  return /*#__PURE__*/React.createElement("li", {
    className: cx((_cx = {}, _defineProperty(_cx, "".concat(blockClass, "__draggable-handleHolder-isOver"), isOver && !disabled), _defineProperty(_cx, "".concat(blockClass, "__draggable-handleHolder-grabbed"), isGrabbed), _defineProperty(_cx, "".concat(blockClass, "__draggable-handleHolder-selected"), selected), _defineProperty(_cx, "".concat(blockClass, "__draggable-handleHolder"), !selected), _cx)),
    ref: ref,
    "aria-selected": isFocused,
    role: "option",
    tabIndex: isFocused ? 0 : -1,
    onKeyPress: function onKeyPress(e) {
      if (e.key === ' ' && e.target === e.currentTarget && !disabled) {
        var positionText = positionLabel.replace('{index}', index + 1).replace('{total}', listData.length);
        var grabAriaText = (isGrabbed ? droppedLabel : grabbedLabel).replace('{itemName}', ariaLabel);
        onGrab(grabAriaText + positionText);
        setIsGrabbed(!isGrabbed);
        e.preventDefault();
      }
    },
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        onArrowKeyDown(e, isGrabbed, index);
      }
    },
    onBlur: function onBlur(e) {
      // handle when focus move to inner elements
      setIsFocusedOnItem(e.currentTarget === e.target);
    },
    onFocus: function onFocus(e) {
      // handle when focus move to li element
      setIsFocusedOnItem(e.currentTarget === e.target);
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "".concat(blockClass, "__shared-ui--assistive-text")
  }, ariaLabel), isDragging && !isOver ? /*#__PURE__*/React.createElement("div", {
    ref: preview,
    className: "${blockClass}__draggable-handleHolder-droppable"
  }, content) : /*#__PURE__*/React.createElement("div", {
    ref: drag,
    "aria-hidden": isFocused && isFocusedOnItem // if focus on li, hide the children from aria
    ,
    className: cx(_defineProperty({}, "".concat(blockClass, "__draggable-handleStyle"), !disabled), ["".concat(blockClass, "__draggable-handleHolder-droppable")])
  }, (!isOver || disabled) && content));
};

DraggableElement.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  disabled: PropTypes.bool,
  droppedLabel: PropTypes.string,
  grabbedLabel: PropTypes.string,
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isFocused: PropTypes.bool.isRequired,
  listData: PropTypes.array.isRequired,
  moveElement: PropTypes.func.isRequired,
  onArrowKeyDown: PropTypes.func.isRequired,
  onGrab: PropTypes.func.isRequired,
  positionLabel: PropTypes.string,
  selected: PropTypes.bool,
  type: PropTypes.string.isRequired
};
export default DraggableElement;